import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import TemplateEditor from '../components/campaign/TemplateEditor';
import ConditionEditor from '../components/campaign/ConditionEditor';
import { campaignTemplates } from '../components/campaign/campaignTemplates';
import { Plus, ChevronLeft, ChevronRight, ArrowLeft, Settings, Trash2, ChevronDown, LayoutTemplate, MessageSquare, Search, Loader2, PlusCircle, Target, Tag, Rocket, BookOpen } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import whatsappLogo from '../components/icons/Whatsapp logo.png';

const getTemplateIcon = (iconName) => {
    switch (iconName) {
        case 'Target': return <Target size={28} strokeWidth={1.75} />;
        case 'Tag': return <Tag size={28} strokeWidth={1.75} />;
        case 'Rocket': return <Rocket size={28} strokeWidth={1.75} />;
        case 'BookOpen': return <BookOpen size={28} strokeWidth={1.75} />;
        default: return <LayoutTemplate size={28} strokeWidth={1.75} />;
    }
};

const CustomDropdown = ({
    value,
    onChange,
    options,
    placeholder,
    disabled = false,
    footerText,
    onFooterClick
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find((opt) => opt.value === value);

    return (
        <div ref={dropdownRef} className="relative">
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`w-full px-3.5 py-2.5 border rounded-lg text-xs bg-white text-left ${disabled
                    ? "opacity-50 cursor-not-allowed border-zinc-200"
                    : isOpen
                    ? "border-zinc-900 ring-1 ring-zinc-900/5 shadow-xs"
                    : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50/50 cursor-pointer shadow-2xs"
                    } flex justify-between items-center transition-all`}
            >
                <span className={`truncate ${selectedOption ? "text-zinc-900 font-medium" : "text-zinc-400"}`}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDown
                    size={14}
                    className={`transition-transform text-zinc-400 shrink-0 ml-2 ${isOpen ? "rotate-180 text-zinc-900" : ""}`}
                />
            </button>

            {isOpen && !disabled && (
                <div className="absolute top-full left-0 right-0 bg-white border border-zinc-200 rounded-lg mt-1 max-h-60 overflow-y-auto z-50 shadow-lg shadow-zinc-950/10">
                    {options.map((option) => (
                        <div
                            key={option.value}
                            onClick={() => {
                                onChange(option.value);
                                setIsOpen(false);
                            }}
                            className={`px-3.5 py-2 text-xs cursor-pointer hover:bg-zinc-50 transition-colors flex items-center justify-between ${value === option.value
                                ? "bg-zinc-100 text-zinc-900 font-semibold"
                                : "text-zinc-700 hover:text-zinc-900"
                                }`}
                        >
                            <span className="truncate">{option.label}</span>
                            {value === option.value && <span className="text-zinc-900 text-xs">✓</span>}
                        </div>
                    ))}
                    {footerText && onFooterClick && (
                        <>
                            <div className="border-t border-zinc-100" />
                            <div
                                onClick={() => {
                                    onFooterClick();
                                    setIsOpen(false);
                                }}
                                className="px-3.5 py-2 text-xs font-semibold text-zinc-900 hover:bg-zinc-50 cursor-pointer transition-colors flex items-center justify-center gap-1.5"
                            >
                                <span className="text-xs">+</span>
                                <span>{footerText}</span>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

const CustomDateTimePicker = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef(null);

    const initialDate = value ? new Date(value) : new Date();
    const [currentMonth, setCurrentMonth] = useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
    const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);

    const [hour, setHour] = useState(selectedDate ? (selectedDate.getHours() % 12 || 12) : 10);
    const [minute, setMinute] = useState(selectedDate ? Math.round(selectedDate.getMinutes() / 5) * 5 : 0);
    const [period, setPeriod] = useState(selectedDate ? (selectedDate.getHours() >= 12 ? 'PM' : 'AM') : 'AM');

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getFormattedValue = () => {
        if (!value) return "Select date & time...";
        const d = new Date(value);
        return d.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDayIndex = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const prevTotalDays = new Date(year, month, 0).getDate();

    const days = [];
    for (let i = firstDayIndex - 1; i >= 0; i--) {
        days.push({ day: prevTotalDays - i, isCurrentMonth: false, date: new Date(year, month - 1, prevTotalDays - i) });
    }
    for (let i = 1; i <= totalDays; i++) {
        days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
        days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
    }

    const handlePrevMonth = () => {
        setCurrentMonth(new Date(year, month - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentMonth(new Date(year, month + 1, 1));
    };

    const updateDateTime = (newDate, newHour, newMin, newPeriod) => {
        if (!newDate) return;
        const finalDate = new Date(newDate);

        let adjustedHour = parseInt(newHour);
        if (newPeriod === 'PM' && adjustedHour < 12) adjustedHour += 12;
        if (newPeriod === 'AM' && adjustedHour === 12) adjustedHour = 0;

        finalDate.setHours(adjustedHour);
        finalDate.setMinutes(parseInt(newMin));
        finalDate.setSeconds(0);
        finalDate.setMilliseconds(0);

        setSelectedDate(finalDate);
        onChange(finalDate.toISOString());
    };

    const handleSelectDay = (dayObj) => {
        setSelectedDate(dayObj.date);
        updateDateTime(dayObj.date, hour, minute, period);
    };

    const handleTimeChange = (type, val) => {
        let newHour = hour;
        let newMin = minute;
        let newPeriod = period;

        if (type === 'hour') {
            newHour = val;
            setHour(val);
        } else if (type === 'minute') {
            newMin = val;
            setMinute(val);
        } else if (type === 'period') {
            newPeriod = val;
            setPeriod(val);
        }

        const dateToUse = selectedDate || new Date();
        updateDateTime(dateToUse, newHour, newMin, newPeriod);
    };

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    return (
        <div className="relative" ref={popoverRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full px-3.5 py-2.5 border rounded-lg bg-white text-xs font-medium text-zinc-900 placeholder:text-zinc-400 focus:outline-none transition-all flex items-center justify-between cursor-pointer ${
                    isOpen ? 'border-zinc-900 ring-1 ring-zinc-900/5 shadow-xs' : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50/50 shadow-2xs'
                }`}
            >
                <div className="flex items-center gap-2">
                    <span>{getFormattedValue()}</span>
                </div>
                <ChevronDown size={14} className={`text-zinc-400 transition-transform ${isOpen ? 'rotate-180 text-zinc-900' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-1.5 bg-white border border-zinc-200 rounded-xl shadow-xl shadow-zinc-950/10 p-4 z-[100] w-[300px] animate-fadeIn">
                    <div className="flex items-center justify-between mb-3">
                        <button
                            type="button"
                            onClick={handlePrevMonth}
                            className="p-1.5 hover:bg-zinc-100 rounded-lg text-zinc-600 transition-colors cursor-pointer"
                        >
                            ◀
                        </button>
                        <span className="text-xs font-semibold text-zinc-900">
                            {monthNames[month]} {year}
                        </span>
                        <button
                            type="button"
                            onClick={handleNextMonth}
                            className="p-1.5 hover:bg-zinc-100 rounded-lg text-zinc-600 transition-colors cursor-pointer"
                        >
                            ▶
                        </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold text-zinc-400 mb-2 uppercase tracking-wider">
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(w => (
                            <div key={w}>{w}</div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1 text-center mb-3">
                        {days.map((dayObj, idx) => {
                            const isSelected = selectedDate &&
                                selectedDate.getDate() === dayObj.date.getDate() &&
                                selectedDate.getMonth() === dayObj.date.getMonth() &&
                                selectedDate.getFullYear() === dayObj.date.getFullYear();

                            const isToday = new Date().toDateString() === dayObj.date.toDateString();

                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => handleSelectDay(dayObj)}
                                    className={`h-7 w-7 text-xs rounded-md transition-colors flex items-center justify-center cursor-pointer ${
                                        isSelected
                                            ? 'bg-zinc-900 text-white font-semibold shadow-2xs'
                                            : isToday
                                            ? 'border border-zinc-900 text-zinc-900 font-bold'
                                            : !dayObj.isCurrentMonth
                                            ? 'text-zinc-300'
                                            : 'text-zinc-700 hover:bg-zinc-100 font-medium'
                                    }`}
                                >
                                    {dayObj.day}
                                </button>
                            );
                        })}
                    </div>

                    <div className="border-t border-zinc-100 pt-3 flex items-center justify-between">
                        <span className="text-xs font-medium text-zinc-600">
                            Time
                        </span>
                        <div className="flex items-center gap-1">
                            <select
                                value={hour}
                                onChange={(e) => handleTimeChange('hour', e.target.value)}
                                className="px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-800 focus:outline-none focus:border-zinc-900"
                            >
                                {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                                    <option key={h} value={h}>{h.toString().padStart(2, '0')}</option>
                                ))}
                            </select>
                            <span className="text-xs font-bold text-zinc-400">:</span>
                            <select
                                value={minute}
                                onChange={(e) => handleTimeChange('minute', e.target.value)}
                                className="px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-semibold text-zinc-800 focus:outline-none focus:border-zinc-900"
                            >
                                {Array.from({ length: 12 }, (_, i) => i * 5).map(m => (
                                    <option key={m} value={m}>{m.toString().padStart(2, '0')}</option>
                                ))}
                            </select>
                            <div className="flex bg-zinc-100 rounded-lg p-0.5 ml-1 border border-zinc-200/60">
                                {['AM', 'PM'].map(p => (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => handleTimeChange('period', p)}
                                        className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${period === p ? 'bg-white text-zinc-900 shadow-2xs' : 'text-zinc-500'
                                            }`}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="w-full mt-3 py-1.5 bg-zinc-900 text-white text-xs font-medium rounded-lg hover:bg-zinc-800 transition-colors shadow-2xs cursor-pointer"
                    >
                        Apply
                    </button>
                </div>
            )}
        </div>
    );
};

/**
 * CampaignManagement modern dashboard for Easychat.
 * Handles listing, creating, deleting campaigns and managing the active live campaign using is_active flag.
 */
const CampaignManagement = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [view, setView] = useState('list'); // 'list' | 'hub' | 'editor' | 'conditions' | 'whatsapp-campaign'

    useEffect(() => {
        if (location.state?.openWhatsAppCampaign) {
            setView('whatsapp-campaign');
            loadWhatsAppResources();
            // Clear location state to prevent repeating on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location]);
    const [hubSubView, setHubSubView] = useState('categories'); // 'categories' | 'web-templates'
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTemplate, setActiveTemplate] = useState(null);
    const { showToast } = useToast();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const CAMPAIGNS_PER_PAGE = 10;
    const [searchQuery, setSearchQuery] = useState("");

    const [customParams, setCustomParams] = useState([]);

    // WhatsApp Campaigns State
    const [segments, setSegments] = useState([]);
    const [waTemplates, setWaTemplates] = useState([]);
    const [selectedSegment, setSelectedSegment] = useState('');
    const [selectedWaTemplate, setSelectedWaTemplate] = useState(null);
    const [waCampaignName, setWaCampaignName] = useState('WhatsApp Campaign');
    const [waTemplateVars, setWaTemplateVars] = useState({});
    const [waScheduleType, setWaScheduleType] = useState('now'); // 'now' | 'schedule'
    const [waScheduleTime, setWaScheduleTime] = useState('');
    const [waHeaderMediaUrl, setWaHeaderMediaUrl] = useState('');
    const [campaignFileName, setCampaignFileName] = useState('');
    const [campaignFileLocalUrl, setCampaignFileLocalUrl] = useState('');
    const [waSubmitting, setWaSubmitting] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000); // Update clock every second
        return () => clearInterval(timer);
    }, []);


    // Sidebar resizer states
    const containerRef = useRef(null);
    const [sidebarWidth, setSidebarWidth] = useState(800);
    const [isResizing, setIsResizing] = useState(false);

    const startResizing = useCallback(() => setIsResizing(true), []);
    const stopResizing = useCallback(() => setIsResizing(false), []);

    const resize = useCallback((e) => {
        if (isResizing && containerRef.current) {
            const newWidth = e.clientX - containerRef.current.getBoundingClientRect().left;
            const maxWidth = window.innerWidth - 100;
            if (newWidth > 300 && newWidth < maxWidth) setSidebarWidth(newWidth);
        }
    }, [isResizing]);

    useEffect(() => {
        if (isResizing) {
            window.addEventListener("mousemove", resize);
            window.addEventListener("mouseup", stopResizing);
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";
        } else {
            window.removeEventListener("mousemove", resize);
            window.removeEventListener("mouseup", stopResizing);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        }
        return () => {
            window.removeEventListener("mousemove", resize);
            window.removeEventListener("mouseup", stopResizing);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [isResizing, resize, stopResizing]);

    const loadWhatsAppResources = async () => {
        try {
            const segRes = await api.getSegments();
            setSegments(segRes?.segments || []);

            const activeAiAgentId = localStorage.getItem("activeAiAgentId");
            const tempRes = await api.request("/whatsapp/templates", { aiAgentId: activeAiAgentId, skipToast: true });
            setWaTemplates((tempRes?.templates || []).filter(t => t.status === "APPROVED"));
        } catch (e) {
            console.error("Failed to load WhatsApp resources:", e);
        }
    };

    useEffect(() => {
        fetchData();
        const loadCustomParams = async () => {
            try {
                const config = await api.getWidgetConfig();
                if (config && config.custom_parameters) {
                    setCustomParams(config.custom_parameters);
                }
            } catch (e) {
                console.error('Failed to load custom parameters:', e);
            }
        };
        loadCustomParams();
    }, []);

    const fetchData = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const campaignsList = await api.getCampaigns();
            const flattened = (campaignsList || []).map(c => ({
                ...(c.data || {}),
                id: c.id,
                priority: c.priority,
                is_active: c.is_active
            }));

            setCampaigns(flattened);
            setError(null);
        } catch (err) {
            setError('Failed to fetch campaigns. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        setHubSubView('categories');
        setView('hub');
    };

    const handleSelectTemplate = (template) => {
        setActiveTemplate({
            ...template,
            id: null,
            name: `${template.name} Campaign`,
            previewButton: template.previewButton || 'Learn More',
            priority: 0
        });
        setView('editor');
    };

    const handleEdit = (campaign) => {
        setActiveTemplate({ ...campaign });
        setView('editor');
    };

    const handleDeleteRequest = (id) => {
        setCampaignToDelete(id);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!campaignToDelete) return;
        setIsDeleting(true);
        try {
            const isWA = campaigns.find(c => c.id === campaignToDelete)?.isWhatsApp;
            if (isWA) {
                await api.deleteWhatsAppCampaign(campaignToDelete);
            } else {
                await api.deleteCampaign(campaignToDelete);
            }
            setCampaigns(prev => prev.filter(c => c.id !== campaignToDelete));
            showToast('Campaign deleted successfully', 'success');
            setIsDeleteModalOpen(false);
        } catch (err) {
            showToast('Failed to delete campaign.', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleToggleLive = async (campaignId, currentStatus) => {
        const newStatus = !currentStatus;
        try {
            await api.updateCampaign(campaignId, { is_active: newStatus });
            setCampaigns(prev => prev.map(c =>
                c.id === campaignId ? { ...c, is_active: newStatus } : c
            ));
            showToast(`Campaign ${newStatus ? 'activated' : 'deactivated'} successfully`, 'success');
        } catch (err) {
            showToast('Failed to update campaign status.', 'error');
        }
    };

    const handleSaveCampaign = async (finalData) => {
        try {
            const payload = {
                ...finalData,
                priority: finalData.conditions?.priority || 0
            };

            if (finalData.id) {
                await api.updateCampaign(finalData.id, payload);
            } else {
                await api.createCampaign(payload);
            }
            await fetchData();
            setView('list');
            setActiveTemplate(null);
            showToast('Campaign saved successfully!', 'success');
        } catch (err) {
            showToast('Failed to save campaign: ' + (err.message || 'Unknown error'), 'error');
        }
    };

    const extractTemplateVarIndices = (template) => {
        if (!template?.components) return [];
        const indices = new Set();
        const pattern = /\{\{(\d+)\}\}/g;
        template.components.forEach(c => {
            if ((c.type === "BODY" || c.type === "HEADER") && c.text) {
                let match;
                while ((match = pattern.exec(c.text)) !== null) {
                    indices.add(match[1]);
                }
            }
        });
        const sorted = Array.from(indices).sort((a, b) => Number(a) - Number(b));
        const buttonsComp = template.components?.find(c => c.type === "BUTTONS");
        const hasDynamicUrl = buttonsComp?.buttons?.find(b => b.type === "URL" && b.url?.includes("{{1}}"));
        if (hasDynamicUrl) {
            sorted.push("button_url");
        }
        return sorted;
    };

    const handleSendWhatsAppCampaign = async () => {
        if (!waCampaignName.trim()) {
            showToast("Please enter a campaign name", "error");
            return;
        }
        if (!selectedSegment) {
            showToast("Please select a target segment", "error");
            return;
        }
        if (!selectedWaTemplate) {
            showToast("Please select a message template", "error");
            return;
        }

        const varIndices = extractTemplateVarIndices(selectedWaTemplate);
        const missingVars = varIndices.filter(idx => !waTemplateVars[idx]?.trim());
        if (missingVars.length > 0) {
            showToast(`Please fill in all variable fields: ${missingVars.map(i => `{{${i}}}`).join(", ")}`, "error");
            return;
        }

        if (waScheduleType === "schedule") {
            if (!waScheduleTime) {
                showToast("Please pick a schedule date and time", "error");
                return;
            }
            if (new Date(waScheduleTime).getTime() <= Date.now()) {
                showToast("Schedule time must be in the future", "error");
                return;
            }
        }

        setWaSubmitting(true);
        try {
            const varsList = varIndices.map(idx => waTemplateVars[idx]);
            const payload = {
                name: waCampaignName,
                segment_id: parseInt(selectedSegment),
                template_name: selectedWaTemplate.name,
                language_code: selectedWaTemplate.language,
                variables: varsList,
                scheduled_time: waScheduleType === "schedule" ? new Date(waScheduleTime).toISOString() : null,
                header_media_url: waHeaderMediaUrl || null
            };

            const res = await api.createWhatsAppCampaign(payload);
            if (res && res.success) {
                showToast(res.message || "WhatsApp campaign created!", "success");
                setSelectedSegment('');
                setSelectedWaTemplate(null);
                setWaTemplateVars({});
                setWaScheduleType('now');
                setWaScheduleTime('');
                setWaHeaderMediaUrl('');
                setCampaignFileName('');
                setCampaignFileLocalUrl('');
                setView('list');
                navigate('/reports');
            }
        } catch (e) {
            showToast("Failed to create WhatsApp campaign: " + (e.message || "Unknown error"), "error");
        } finally {
            setWaSubmitting(false);
        }
    };

    /* ── Template Hub View ── */
    if (view === 'hub') {
        if (hubSubView === 'categories') {
            return (
                <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif]">
                    <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
                        <div className="flex items-center gap-3 pb-6 border-b border-zinc-100">
                            <button
                                onClick={() => setView('list')}
                                className="w-8 h-8 flex items-center justify-center border border-zinc-200 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors cursor-pointer"
                            >
                                <ArrowLeft size={15} />
                            </button>
                            <div>
                                <h1 className="text-lg font-bold text-zinc-900 tracking-tight">Select Campaign Channel</h1>
                                <p className="text-xs text-zinc-500 mt-0.5">Choose a campaign type to begin configuration.</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            {/* Web Templates Card */}
                            <div
                                onClick={() => setHubSubView('web-templates')}
                                className="group bg-white rounded-xl border border-zinc-200/80 p-5 cursor-pointer transition-colors hover:border-zinc-300 hover:shadow-sm flex gap-4"
                            >
                                <div className="w-16 h-16 flex-shrink-0 bg-zinc-100 rounded-xl border border-zinc-200 flex items-center justify-center text-zinc-700">
                                    <LayoutTemplate size={28} strokeWidth={1.75} />
                                </div>
                                <div className="flex-1 flex flex-col justify-between">
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <h3 className="text-sm font-semibold text-zinc-900">Web Widget Campaigns</h3>
                                            <span className="text-[10px] font-semibold bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded border border-zinc-200">
                                                In-App
                                            </span>
                                        </div>
                                        <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2">
                                            Display behavioral popups, banners, and personalized triggers on your website chat widget.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* WhatsApp Campaign Card */}
                            <div
                                onClick={() => {
                                    setWaCampaignName('WhatsApp Campaign');
                                    setSelectedSegment('');
                                    setSelectedWaTemplate(null);
                                    setWaTemplateVars({});
                                    setWaScheduleType('now');
                                    setWaScheduleTime('');
                                    loadWhatsAppResources();
                                    setView('whatsapp-campaign');
                                }}
                                className="group bg-white rounded-xl border border-zinc-200/80 p-5 cursor-pointer transition-colors hover:border-zinc-300 hover:shadow-sm flex gap-4"
                            >   
                                <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center">
                                    <img src={whatsappLogo} alt="WhatsApp" className="w-full h-full object-contain" />
                                </div>
                                <div className="flex-1 flex flex-col justify-between">
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <h3 className="text-sm font-semibold text-zinc-900">WhatsApp Broadcasts</h3>
                                            <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded border border-emerald-200/60">
                                                Direct
                                            </span>
                                        </div>
                                        <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2">
                                            Broadcast Meta-approved WhatsApp message templates to segmented contact audiences.
                                        </p>
                                    </div>
                                    
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif]">
                <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
                    <div className="flex items-center gap-3 pb-6 border-b border-zinc-100">
                        <button
                            onClick={() => setHubSubView('categories')}
                            className="w-8 h-8 flex items-center justify-center border border-zinc-200 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors cursor-pointer"
                        >
                            <ArrowLeft size={15} />
                        </button>
                        <div>
                            <h1 className="text-lg font-bold text-zinc-900 tracking-tight">Web Templates</h1>
                            <p className="text-xs text-zinc-500 mt-0.5">Select a pre-designed layout or start with a custom blank canvas.</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6">
                        {/* Custom Campaign Card */}
                        <div
                            onClick={() => handleSelectTemplate({ name: 'New Campaign', previewTitle: 'Welcome!', previewContent: 'Edit your content here...', bgColor: '#f3f4f6', image: '✨' })}
                            className="group bg-white rounded-xl border border-zinc-200/80 p-5 cursor-pointer transition-colors hover:border-zinc-300 hover:shadow-sm flex gap-4"
                        >
                            <div className="w-16 h-16 flex-shrink-0 bg-zinc-100 rounded-xl border border-zinc-200 flex items-center justify-center text-zinc-700">
                                <PlusCircle size={28} strokeWidth={1.75} />
                            </div>
                            <div className="flex-1 flex flex-col justify-between">
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <h3 className="text-sm font-semibold text-zinc-900">Custom Campaign</h3>
                                        <span className="text-[10px] font-semibold bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded border border-zinc-200">
                                            Blank
                                        </span>
                                    </div>
                                    <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2">Start from scratch with a blank canvas and build your own target flow.</p>
                                </div>
                                <div className="pt-2 text-[11px] font-medium text-zinc-500 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
                                    <span>Fully custom triggers & flexible layout</span>
                                </div>
                            </div>
                        </div>

                        {campaignTemplates.map(tmp => (
                            <div
                                key={tmp.id}
                                onClick={() => handleSelectTemplate(tmp)}
                                className="group bg-white rounded-xl border border-zinc-200/80 p-5 cursor-pointer transition-colors hover:border-zinc-300 hover:shadow-sm flex gap-4"
                            >
                                <div className="w-16 h-16 flex-shrink-0 bg-zinc-100 rounded-xl border border-zinc-200 flex items-center justify-center text-zinc-700">
                                    {getTemplateIcon(tmp.icon)}
                                </div>
                                <div className="flex-1 flex flex-col justify-between">
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <h3 className="text-sm font-semibold text-zinc-900">{tmp.name}</h3>
                                            {tmp.badge && (
                                                <span className="text-[10px] font-semibold bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded border border-zinc-200">
                                                    {tmp.badge}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2">{tmp.description}</p>
                                    </div>
                                    <div className="pt-2 text-[11px] font-medium text-zinc-500 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
                                        <span>{tmp.highlights ? tmp.highlights[0] : 'Ready to launch'}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (view === 'whatsapp-campaign') {
        const varIndices = selectedWaTemplate ? extractTemplateVarIndices(selectedWaTemplate) : [];
        const selectedBodyComponent = selectedWaTemplate?.components?.find(c => c.type === 'BODY');
        const selectedHeaderComponent = selectedWaTemplate?.components?.find(c => c.type === 'HEADER');
        const selectedFooterComponent = selectedWaTemplate?.components?.find(c => c.type === 'FOOTER');
        const selectedButtonsComponent = selectedWaTemplate?.components?.find(c => c.type === 'BUTTONS');

        // Build live preview text by substituting variables into the template body
        const getLivePreviewText = () => {
            if (!selectedBodyComponent?.text) return 'Message body preview...';
            let text = selectedBodyComponent.text;
            varIndices.forEach(idx => {
                const val = waTemplateVars[idx]?.trim();
                text = text.replace(new RegExp(`\\{\\{${idx}\\}\\}`, 'g'), val ? val : `{{${idx}}}`);
            });
            return text;
        };

        const now = new Date();
        const previewTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

        const segmentOptions = segments.map(seg => ({
            value: String(seg.id),
            label: `${seg.name} (${seg.contact_ids?.length || 0} contacts)`
        }));

        const templateOptions = waTemplates.map(tmp => ({
            value: tmp.name,
            label: `${tmp.name} (${tmp.language})`
        }));

        return (
            <div
                ref={containerRef}
                className="flex"
                style={{ height: 'calc(100vh - 84px)', background: '#f8fafcff' }}
            >
                {/* ── Left Panel ── */}
                <div className="flex flex-col bg-white border-r border-zinc-100" style={{ width: sidebarWidth }}>

                    {/* Header */}
                    <header className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setView('hub')}
                                className="w-8 h-8 flex items-center justify-center border border-zinc-200 rounded-lg text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors cursor-pointer flex-shrink-0"
                                title="Back to Campaigns"
                            >
                                <ArrowLeft size={15} />
                            </button>
                            <div>
                                <input
                                    type="text"
                                    value={waCampaignName}
                                    onChange={(e) => setWaCampaignName(e.target.value)}
                                    placeholder="WhatsApp Campaign Name"
                                    className="text-base font-bold text-zinc-900 tracking-tight border-b border-transparent hover:border-zinc-200 focus:border-zinc-900 outline-none bg-transparent py-0.5 transition-colors placeholder:text-zinc-400"
                                />
                                <p className="text-xs text-zinc-500 mt-0.5">Configure target audience and template for WhatsApp broadcast.</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate("/whatsapp-templates", { state: { from: 'campaign', campaignName: waCampaignName } })}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 text-xs font-semibold rounded-lg shadow-2xs transition-colors cursor-pointer flex-shrink-0"
                        >
                            <MessageSquare size={13} className="text-zinc-500" />
                            <span>Templates</span>
                        </button>
                    </header>

                    {/* Form Body */}
                    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">

                        {/* — Target Segment — */}
                        <div className="space-y-1.5">
                            <div>
                                <label className="text-xs font-semibold text-zinc-900 block">Target Segment</label>
                                <p className="text-xs text-zinc-500 mt-0.5">Choose the audience group to receive this campaign.</p>
                            </div>
                            <div className="pt-1">
                                <CustomDropdown
                                    value={selectedSegment}
                                    onChange={(val) => setSelectedSegment(val)}
                                    options={segmentOptions}
                                    placeholder="Select a contact segment..."
                                    footerText="Add Segment"
                                    onFooterClick={() => navigate("/segments")}
                                />
                            </div>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-zinc-100" />

                        {/* — Message Template — */}
                        <div className="space-y-1.5">
                            <div>
                                <label className="text-xs font-semibold text-zinc-900 block">Message Template</label>
                                <p className="text-xs text-zinc-500 mt-0.5">Only Meta-approved templates are listed here.</p>
                            </div>
                            <div className="pt-1">
                                <CustomDropdown
                                    value={selectedWaTemplate ? selectedWaTemplate.name : ''}
                                    onChange={(val) => {
                                        const matched = waTemplates.find(t => t.name === val);
                                        setSelectedWaTemplate(matched || null);
                                        setWaTemplateVars({});
                                    }}
                                    options={templateOptions}
                                    placeholder="Select an approved template..."
                                    footerText="Add Template"
                                    onFooterClick={() => navigate("/whatsapp-templates", { state: { from: 'campaign', campaignName: waCampaignName } })}
                                />
                            </div>
                        </div>

                        {/* — Header Media URL (Only for templates with media headers) — */}
                        {selectedWaTemplate && selectedWaTemplate.components?.some(
                            c => c.type === "HEADER" && ["IMAGE", "VIDEO", "DOCUMENT"].includes(c.format)
                        ) && (
                            <>
                                <div className="border-t border-zinc-100" />
                                <div className="space-y-2">
                                    <div>
                                        <label className="text-xs font-semibold text-zinc-900 block">
                                            Header {selectedWaTemplate.components.find(c => c.type === "HEADER")?.format.charAt(0) + selectedWaTemplate.components.find(c => c.type === "HEADER")?.format.slice(1).toLowerCase()} File
                                        </label>
                                        <p className="text-xs text-zinc-500 mt-0.5">Upload the media asset to include in the campaign blast.</p>
                                    </div>
                                    <div className="pt-1">
                                        <input
                                            type="file"
                                            accept={selectedWaTemplate.components.find(c => c.type === "HEADER")?.format === "IMAGE" ? "image/png, image/jpeg" : selectedWaTemplate.components.find(c => c.type === "HEADER")?.format === "VIDEO" ? "video/mp4" : "application/pdf"}
                                            onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                
                                                setCampaignFileLocalUrl(URL.createObjectURL(file));
                                                setCampaignFileName(file.name);

                                                const formData = new FormData();
                                                formData.append("file", file);
                                                
                                                showToast("Uploading media asset to Meta...", "info");
                                                try {
                                                    const res = await api.request("/whatsapp/campaigns/upload-media", {
                                                        method: "POST",
                                                        body: formData
                                                    });
                                                    if (res && res.media_id) {
                                                        const formatType = selectedWaTemplate.components.find(c => c.type === "HEADER")?.format.toLowerCase() || "image";
                                                        setWaHeaderMediaUrl(`media_id:${formatType}:${res.media_id}`);
                                                        showToast("Media uploaded successfully!", "success");
                                                    }
                                                } catch (err) {
                                                    showToast("Failed to upload media: " + (err.message || "Upload failed"), "error");
                                                }
                                            }}
                                            style={{ display: "none" }}
                                            id="campaign-media-upload"
                                        />

                                        {!waHeaderMediaUrl ? (
                                            <div 
                                                className="border-2 border-dashed border-zinc-200 hover:border-zinc-300 rounded-xl p-5 text-center bg-zinc-50/50 hover:bg-zinc-50 transition-colors cursor-pointer"
                                                onClick={() => document.getElementById("campaign-media-upload").click()}
                                            >
                                                <div className="text-xs text-zinc-600 font-medium">Drag and drop to upload</div>
                                                <div className="text-xs text-zinc-900 font-semibold mt-1">
                                                    Or <span className="underline">choose files on your device</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-lg p-3">
                                                <div className="flex items-center gap-2.5">
                                                    {campaignFileLocalUrl && selectedWaTemplate.components.find(c => c.type === "HEADER")?.format === "IMAGE" && (
                                                        <img 
                                                            src={campaignFileLocalUrl} 
                                                            alt="Thumbnail" 
                                                            className="w-9 h-9 object-cover rounded" 
                                                        />
                                                    )}
                                                    {campaignFileLocalUrl && selectedWaTemplate.components.find(c => c.type === "HEADER")?.format === "VIDEO" && (
                                                        <video 
                                                            src={campaignFileLocalUrl} 
                                                            className="w-9 h-9 object-cover rounded" 
                                                        />
                                                    )}
                                                    {selectedWaTemplate.components.find(c => c.type === "HEADER")?.format === "DOCUMENT" && (
                                                        <div className="w-9 h-9 bg-zinc-200 flex items-center justify-center rounded text-[10px] font-bold text-zinc-700">
                                                            PDF
                                                        </div>
                                                    )}
                                                    <span className="text-xs font-semibold text-zinc-900 truncate max-w-[200px]">
                                                        {campaignFileName || "campaign_file"}
                                                    </span>
                                                </div>
                                                <button 
                                                    type="button"
                                                    onClick={() => {
                                                        setWaHeaderMediaUrl("");
                                                        setCampaignFileName("");
                                                        setCampaignFileLocalUrl("");
                                                    }}
                                                    className="text-xs font-semibold text-zinc-400 hover:text-zinc-700 p-1 rounded hover:bg-zinc-200/50 transition-colors"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* — Template Variables — */}
                        {selectedWaTemplate && varIndices.length > 0 && (
                            <>
                                <div className="border-t border-zinc-100" />
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-xs font-semibold text-zinc-900 block">Template Variables</label>
                                        <p className="text-xs text-zinc-500 mt-0.5">Use <code className="font-mono bg-zinc-100 px-1 py-0.5 rounded text-zinc-700 text-[11px]">{'{name}'}</code> or <code className="font-mono bg-zinc-100 px-1 py-0.5 rounded text-zinc-700 text-[11px]">{'{email}'}</code> for dynamic contact values.</p>
                                    </div>
                                    <div className="space-y-3 pt-1">
                                        {varIndices.map(idx => {
                                            const isButtonUrl = idx === "button_url";
                                            return (
                                                <div key={idx} className="space-y-1">
                                                    <label className="block text-[11px] font-medium text-zinc-600">
                                                        {isButtonUrl ? "Website URL Parameter" : `Variable {{${idx}}}`}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={waTemplateVars[idx] || ''}
                                                        onChange={(e) => setWaTemplateVars(prev => ({ ...prev, [idx]: e.target.value }))}
                                                        placeholder={isButtonUrl ? "Value to append to dynamic website link (e.g. ORD12345)" : `Value for {{${idx}}} — or {name}, {email}`}
                                                        className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 placeholder:text-zinc-400 transition-colors shadow-2xs"
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Divider */}
                        <div className="border-t border-zinc-100" />

                        {/* — Delivery Option — */}
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-semibold text-zinc-900 block">Delivery Option</label>
                                <p className="text-xs text-zinc-500 mt-0.5">Select when this campaign should be broadcast.</p>
                            </div>

                            {/* Segmented Control */}
                            <div className="inline-flex items-center bg-zinc-100/80 rounded-lg p-1 border border-zinc-200/60">
                                <button
                                    type="button"
                                    onClick={() => setWaScheduleType('now')}
                                    className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all ${waScheduleType === 'now' ? 'bg-white text-zinc-900 shadow-2xs' : 'text-zinc-500 hover:text-zinc-800'}`}
                                >
                                    Send Immediately
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setWaScheduleType('schedule')}
                                    className={`px-3.5 py-1.5 rounded-md text-xs font-semibold transition-all ${waScheduleType === 'schedule' ? 'bg-white text-zinc-900 shadow-2xs' : 'text-zinc-500 hover:text-zinc-800'}`}
                                >
                                    Schedule for Later
                                </button>
                            </div>

                            {waScheduleType === 'schedule' && (
                                <div className="space-y-1.5 pt-1">
                                    <label className="block text-xs font-medium text-zinc-600">Dispatch Date & Time</label>
                                    <CustomDateTimePicker
                                        value={waScheduleTime}
                                        onChange={(val) => setWaScheduleTime(val)}
                                    />
                                </div>
                            )}
                        </div>

                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-zinc-100 bg-white flex items-center justify-end gap-2.5 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setView('hub')}
                            className="px-4 py-2 text-xs font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={waSubmitting}
                            onClick={handleSendWhatsAppCampaign}
                            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 active:scale-[0.98] cursor-pointer shadow-xs"
                        >
                            {waSubmitting && <Loader2 size={13} className="animate-spin" />}
                            <span>
                                {waSubmitting ? 'Processing...' : waScheduleType === 'schedule' ? 'Schedule Broadcast' : 'Send Campaign'}
                            </span>
                            {!waSubmitting && <ChevronRight size={13} />}
                        </button>
                    </div>
                </div>

                {/* ── Resizer ── */}
                <div
                    className={`w-1 flex-shrink-0 cursor-col-resize relative group transition-colors ${isResizing ? "bg-zinc-900" : "bg-zinc-200 hover:bg-zinc-400"}`}
                    onMouseDown={startResizing}
                />

                {/* ── Preview Panel ── */}
                <main className="flex-1 flex flex-col items-center justify-center p-10 bg-[#f8fafc] overflow-hidden">

                    {/* iPhone Mockup Frame */}
                    <div className="relative w-[320px] h-[600px] bg-[#161617] rounded-[48px] p-3 shadow-[0_25px_60px_rgba(0,0,0,0.45)] border-[5px] border-[#2c2c2e] flex flex-col flex-shrink-0 transition-all select-none">
                        {/* Silent Switch & Volume Buttons */}
                        <div className="absolute -left-[6px] top-[88px] w-[4px] h-[28px] bg-[#3a3a3c] rounded-l-sm" />
                        <div className="absolute -left-[6px] top-[130px] w-[4px] h-[52px] bg-[#3a3a3c] rounded-l-sm" />
                        <div className="absolute -left-[6px] top-[192px] w-[4px] h-[52px] bg-[#3a3a3c] rounded-l-sm" />
                        {/* Power Button */}
                        <div className="absolute -right-[6px] top-[138px] w-[4px] h-[64px] bg-[#3a3a3c] rounded-r-sm" />

                        {/* ── iPhone Screen ── */}
                        <div className="relative flex-1 rounded-[38px] overflow-hidden flex flex-col" style={{
                            backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')",
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}>

                            {/* Classic iPhone Notch */}
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[148px] h-[28px] bg-black rounded-b-[20px] z-30 flex items-center justify-center gap-3">
                                <div className="w-10 h-[4px] bg-[#2a2a2a] rounded-full" />
                                <div className="w-[9px] h-[9px] bg-[#0a0d14] rounded-full border border-[#222] flex items-center justify-center">
                                    <div className="w-[4px] h-[4px] bg-[#162050] rounded-full" />
                                </div>
                            </div>

                            {/* Status Bar */}
                            <div className="h-[38px] px-5 pt-1 flex justify-between items-center text-black text-[11px] font-semibold z-20 select-none bg-white/95">
                                <span className="font-bold tracking-tight">
                                    {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                                </span>
                                <div className="flex items-center gap-[5px]">
                                    <div className="flex items-end gap-[2px] h-[10px]">
                                        <div className="w-[2px] h-[3px] bg-black rounded-[1px]" />
                                        <div className="w-[2px] h-[5px] bg-black rounded-[1px]" />
                                        <div className="w-[2px] h-[7px] bg-black rounded-[1px]" />
                                        <div className="w-[2px] h-[10px] bg-black rounded-[1px]" />
                                    </div>
                                    <svg width="13" height="10" viewBox="0 0 24 18" fill="black">
                                        <path d="M12 4.5C8.5 4.5 5.3 5.8 2.9 8L0 5.1C3.1 2 7.4 0 12 0s8.9 2 12 5.1L21.1 8C18.7 5.8 15.5 4.5 12 4.5z" />
                                        <path d="M12 9c-2.2 0-4.2.9-5.6 2.3L4 9C6.1 7 8.9 5.8 12 5.8s5.9 1.2 8 3.2l-2.4 2.3C16.2 9.9 14.2 9 12 9z" />
                                        <path d="M12 13.5c-1.1 0-2.1.4-2.8 1.1L12 18l2.8-3.4c-.7-.7-1.7-1.1-2.8-1.1z" />
                                    </svg>
                                    <div className="flex items-center gap-[1px]">
                                        <div className="w-[20px] h-[10px] border border-black rounded-[3px] p-[1.5px] flex items-center">
                                            <div className="h-full w-[13px] bg-black rounded-[1.5px]" />
                                        </div>
                                        <div className="w-[2px] h-[5px] bg-black rounded-r-[2px]" />
                                    </div>
                                </div>
                            </div>

                            {/* WhatsApp Chat Header */}
                            <div className="h-[54px] bg-white/97 backdrop-blur-md border-b border-zinc-200/60 px-3 flex items-center justify-between z-30 select-none">
                                <div className="flex items-center gap-1.5">
                                    <div className="flex items-center gap-[2px]">
                                        <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                                            <path d="M7 1L1 7l6 6" stroke="#007aff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <span className="text-[#007aff] text-[12px] font-semibold leading-none">3</span>
                                    </div>
                                    <div className="w-[36px] h-[36px] rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white font-extrabold text-[11px] shadow-sm flex-shrink-0">
                                        EA
                                    </div>
                                    <div className="flex flex-col leading-tight">
                                        <span className="text-[12px] font-extrabold text-zinc-900 tracking-tight">EasyChat</span>
                                        <span className="text-[9px] text-zinc-500 font-medium">online</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <svg width="19" height="14" viewBox="0 0 24 18" fill="none" stroke="#007aff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="1" y="1" width="15" height="16" rx="3" />
                                        <path d="M16 6.5l7-4.5v13l-7-4.5" />
                                    </svg>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#007aff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6 19.8 19.8 0 01-3.1-8.7A2 2 0 014.1 2h3a2 2 0 012 1.7c.1 1 .4 2 .7 2.9a2 2 0 01-.5 2L8.1 9.9a16 16 0 006 6l1.3-1.3a2 2 0 012-.5c.9.3 1.9.6 2.9.7a2 2 0 011.7 2z" />
                                    </svg>
                                </div>
                            </div>

                            {/* Chat Area */}
                            <div className="flex-1 overflow-y-auto px-3 py-2 flex flex-col gap-3">
                                {/* Today pill */}
                                <div className="flex justify-center py-1">
                                    <span className="text-[9px] text-zinc-600 font-semibold bg-white/75 backdrop-blur-sm px-3 py-[3px] rounded-full shadow-sm">Today</span>
                                </div>

                                {/* Template message bubble */}
                                <div style={{
                                    background: '#fff',
                                    borderRadius: '14px',
                                    borderTopLeftRadius: '2px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                    padding: '8px 10px 6px 10px',
                                    maxWidth: '90%',
                                    alignSelf: 'flex-start',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '3px',
                                }}>
                                    {selectedHeaderComponent?.format === 'IMAGE' && (
                                        <div style={{ width: '100%', height: '100px', background: '#f1f5f9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '4px' }}>
                                            {campaignFileLocalUrl ? (
                                                <img src={campaignFileLocalUrl} alt="Header Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <span style={{ fontSize: '24px' }}>🖼️</span>
                                            )}
                                        </div>
                                    )}
                                    {selectedHeaderComponent?.format === 'VIDEO' && (
                                        <div style={{ width: '100%', height: '100px', background: '#f1f5f9', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '4px' }}>
                                            {campaignFileLocalUrl ? (
                                                <video src={campaignFileLocalUrl} controls autoPlay muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <span style={{ fontSize: '24px' }}>📹</span>
                                            )}
                                        </div>
                                    )}
                                    {selectedHeaderComponent?.format === 'DOCUMENT' && (
                                        <div style={{ width: '100%', height: '40px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 10px', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '16px' }}>📄</span>
                                            <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>
                                                {campaignFileName || 'document.pdf'}
                                            </span>
                                        </div>
                                    )}
                                    {selectedHeaderComponent?.format === 'TEXT' && selectedHeaderComponent.text && (
                                        <p style={{ fontWeight: 800, fontSize: '12px', color: '#0f172a', margin: 0 }}>
                                            {selectedHeaderComponent.text}
                                        </p>
                                    )}
                                    <p style={{ fontSize: '12px', color: '#1e293b', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                                        {getLivePreviewText()}
                                    </p>
                                    {selectedFooterComponent?.text && (
                                        <p style={{ fontSize: '9px', color: '#94a3b8', margin: '1px 0 0' }}>
                                            {selectedFooterComponent.text}
                                        </p>
                                    )}
                                    <span style={{ fontSize: '8px', color: '#94a3b8', alignSelf: 'flex-end', marginTop: '1px' }}>
                                        {previewTime}
                                    </span>
                                    {selectedButtonsComponent?.buttons?.length > 0 && (
                                        <div style={{ borderTop: '1px solid #e2e8f0', margin: '4px -10px 0 -10px', display: 'flex', flexDirection: 'column' }}>
                                            {selectedButtonsComponent.buttons.slice(0, 3).map((btn, i) => (
                                                <div key={i} style={{
                                                    padding: '8px 10px', borderBottom: i < selectedButtonsComponent.buttons.length - 1 ? '1px solid #e2e8f0' : 'none',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                                                    fontSize: '11px', fontWeight: 700, color: '#007aff',
                                                }}>
                                                    {btn.type === 'PHONE_NUMBER' && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#007aff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6 19.8 19.8 0 01-3.1-8.7A2 2 0 014.1 2h3a2 2 0 012 1.7c.1 1 .4 2 .7 2.9a2 2 0 01-.5 2L8.1 9.9a16 16 0 006 6l1.3-1.3a2 2 0 012-.5c.9.3 1.9.6 2.9.7a2 2 0 011.7 2z" /></svg>}
                                                    {btn.type === 'URL' && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#007aff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>}
                                                    {btn.type === 'QUICK_REPLY' && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 00-4-4H4" /></svg>}
                                                    <span>{btn.text}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* WhatsApp Input Bar & Bottom Area Unified */}
                            <div className="bg-[#f6f6f6] border-t border-zinc-200/50 flex flex-col z-30 select-none pb-2 pt-2">
                                <div className="h-[44px] px-3 flex items-center gap-3">
                                    {/* Plus button */}
                                    <button className="flex-shrink-0 flex items-center justify-center">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#007aff" strokeWidth="2.2" strokeLinecap="round">
                                            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                        </svg>
                                    </button>

                                    {/* White input field */}
                                    <div className="flex-1 h-[32px] bg-white rounded-[18px] border border-zinc-200/80 flex items-center px-3 justify-end">
                                        {/* Sticker folder icon */}
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#007aff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M16 3H8C5.24 3 3 5.24 3 8v8c0 2.76 2.24 5 5 5h5l5-5V8c0-2.76-2.24-5-5-5z" />
                                            <path d="M13 21v-3c0-1.1.9-2 2-2h3" />
                                        </svg>
                                    </div>

                                    {/* Camera button */}
                                    <button className="flex-shrink-0">
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#007aff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                            <circle cx="12" cy="13" r="4" />
                                        </svg>
                                    </button>

                                    {/* Mic button */}
                                    <button className="flex-shrink-0">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#007aff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                            <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
                                            <line x1="12" y1="19" x2="12" y2="23" />
                                            <line x1="8" y1="23" x2="16" y2="23" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Home Indicator */}
                                <div className="h-[20px] flex items-center justify-center select-none pt-1">
                                    <div className="w-28 h-[4px] bg-black rounded-full" />
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    if (view === 'editor') {
        return (
            <TemplateEditor
                template={activeTemplate}
                onBack={() => setView(activeTemplate.id ? 'list' : 'hub')}
                onNext={(data) => {
                    setActiveTemplate(data);
                    setView('conditions');
                }}
            />
        );
    }

    if (view === 'conditions') {
        return (
            <ConditionEditor
                campaignData={activeTemplate}
                customParams={customParams}
                onBack={() => setView('editor')}
                onSave={handleSaveCampaign}
            />
        );
    }

    /* ── Filtered campaigns & metrics ── */
    const filteredCampaigns = campaigns.filter(c => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (c.name || '').toLowerCase().includes(q) || (c.previewTitle || '').toLowerCase().includes(q);
    });

    const paginatedCampaigns = filteredCampaigns.slice((currentPage - 1) * CAMPAIGNS_PER_PAGE, currentPage * CAMPAIGNS_PER_PAGE);

    /* ── Main List View ── */
    return (
        <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif]">
            <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
                    <div>
                        <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Campaign Orchestration</h1>
                        <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
                            Boost user engagement with automated behavioral triggers, widget banners, and WhatsApp broadcasts.
                        </p>
                    </div>
                    <button
                        onClick={handleCreateNew}
                        className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] self-start sm:self-auto cursor-pointer"
                    >
                        <Plus size={14} strokeWidth={2.5} />
                        <span>Create Campaign</span>
                    </button>
                </div>

                {/* Search Bar */}
                <div className="pt-5 pb-4">
                    <div className="relative w-full max-w-sm">
                        <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
                        />
                        <input
                            type="text"
                            placeholder="Search campaigns by name..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="w-full pl-9 pr-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:bg-white text-zinc-900 placeholder:text-zinc-400 transition-colors"
                        />
                    </div>
                </div>

                {/* Main Content */}
                {loading ? (
                    <div className="flex flex-col items-center justify-center min-h-[300px] bg-white rounded-xl border border-zinc-200/80 p-8">
                        <Loader2 size={24} className="animate-spin text-zinc-400 mb-3" />
                        <p className="text-xs font-medium text-zinc-500">Loading campaigns...</p>
                    </div>
                ) : error ? (
                    <div className="p-4 rounded-xl border border-red-200 bg-red-50 text-red-700 text-xs font-medium">
                        {error}
                    </div>
                ) : filteredCampaigns.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-zinc-200/80 text-center p-6">
              <h3 className="text-sm font-semibold text-zinc-900 mb-1">
                            {searchQuery ? "No matching campaigns" : "No campaigns created yet"}
                        </h3>
                        <p className="text-xs text-zinc-500 max-w-sm mb-4">
                            {searchQuery
                                ? `No campaigns found matching "${searchQuery}". Try a different search term.`
                                : "Create targeted behavioral banners and trigger campaigns to engage your users."}
                        </p>
                        {!searchQuery && (
                            <button
                                onClick={handleCreateNew}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white rounded-lg text-xs font-semibold hover:bg-zinc-800 transition-colors cursor-pointer"
                            >
                                <Plus size={13} />
                                <span>Create your first campaign</span>
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {paginatedCampaigns.map((campaign) => (
                                <div
                                    key={campaign.id}
                                    className="bg-white rounded-xl border border-zinc-200/80 overflow-hidden shadow-2xs hover:border-zinc-300 hover:shadow-xs transition-colors flex flex-col justify-between group"
                                >
                                    {/* Campaign Visual Banner */}
                                    <div
                                        className="h-32 flex items-center justify-center relative overflow-hidden"
                                        style={{ background: campaign.bgColor || "#f4f4f5" }}
                                    >
                                        {campaign.image && (campaign.image.startsWith("data:") || campaign.image.includes("/") || campaign.image.includes(".webp") || campaign.image.includes("static")) ? (
                                            <img
                                                src={campaign.image}
                                                alt={campaign.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            />
                                        ) : (
                                            <span className="text-4xl group-hover:scale-110 transition-transform">
                                                {campaign.image || "🏷️"}
                                            </span>
                                        )}

                                        {/* Status badge */}
                                        <div className="absolute top-2.5 left-2.5">
                                            <span
                                                className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                                    campaign.is_active
                                                        ? "bg-emerald-600 text-white shadow-2xs"
                                                        : "bg-zinc-900/60 backdrop-blur-sm text-white"
                                                }`}
                                            >
                                                {campaign.is_active ? "Live" : "Draft"}
                                            </span>
                                        </div>

                                        {/* Priority badge */}
                                        {campaign.priority > 0 && (
                                            <div className="absolute top-2.5 right-2.5">
                                                <span className="px-2 py-0.5 rounded text-[10px] bg-zinc-900 text-white font-semibold shadow-2xs">
                                                    P{campaign.priority}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Card Details */}
                                    <div className="p-4 flex-1 flex flex-col justify-between">
                                        <div>
                                            <h3 className="text-sm font-semibold text-zinc-900 truncate mb-1">
                                                {campaign.name}
                                            </h3>
                                            <p className="text-xs text-zinc-500 line-clamp-2 min-h-[32px]">
                                                {campaign.previewTitle || "No preview description configured"}
                                            </p>
                                        </div>

                                        {/* Action Bar */}
                                        <div className="flex items-center gap-2 pt-4 mt-3 border-t border-zinc-100">
                                            <button
                                                onClick={() => handleToggleLive(campaign.id, campaign.is_active)}
                                                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-colors cursor-pointer text-center ${
                                                    campaign.is_active
                                                        ? "bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border border-zinc-200"
                                                        : "bg-zinc-900 hover:bg-zinc-800 text-white shadow-2xs"
                                                }`}
                                            >
                                                {campaign.is_active ? "Deactivate" : "Go Live"}
                                            </button>
                                            <button
                                                onClick={() => handleEdit(campaign)}
                                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors cursor-pointer"
                                                title="Edit Campaign"
                                            >
                                                <Settings size={14} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteRequest(campaign.id)}
                                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-400 hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors cursor-pointer"
                                                title="Delete Campaign"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {Math.ceil(filteredCampaigns.length / CAMPAIGNS_PER_PAGE) > 1 && (
                            <div className="flex items-center justify-between pt-4 border-t border-zinc-100">
                                <span className="text-xs text-zinc-500">
                                    Showing {(currentPage - 1) * CAMPAIGNS_PER_PAGE + 1} to{" "}
                                    {Math.min(currentPage * CAMPAIGNS_PER_PAGE, filteredCampaigns.length)} of{" "}
                                    {filteredCampaigns.length} campaigns
                                </span>
                                <div className="flex items-center gap-1.5">
                                    <button
                                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-zinc-200 rounded-lg text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 disabled:hover:bg-transparent cursor-pointer"
                                    >
                                        <ChevronLeft size={14} />
                                        <span>Previous</span>
                                    </button>
                                    <button
                                        onClick={() =>
                                            setCurrentPage((prev) =>
                                                Math.min(prev + 1, Math.ceil(filteredCampaigns.length / CAMPAIGNS_PER_PAGE))
                                            )
                                        }
                                        disabled={currentPage === Math.ceil(filteredCampaigns.length / CAMPAIGNS_PER_PAGE)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-zinc-200 rounded-lg text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 disabled:hover:bg-transparent cursor-pointer"
                                    >
                                        <span>Next</span>
                                        <ChevronRight size={14} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Delete Modal */}
            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Campaign"
                message="Are you sure you want to permanently remove this campaign? This action cannot be undone."
                loading={isDeleting}
            />
        </div>
    );
};

export default CampaignManagement;
